/**
 * ProspectusCover — the INVESTMENT-MEMORANDUM title page (mockup §01
 * `.t-prospectus` / `.pr-*`). A more financial, "tombstone" sibling of the
 * EditorialMastheadCover: a double-rule confidential eyebrow leads, the project
 * name reads in the display serif at headline scale, an italic standfirst sets
 * the thesis, then a DARK "tombstone" verdict band carries the deterministic
 * verdict word + the headline-metric line, a 4-up key-terms rail surfaces the
 * mono figures, and a mono imprint footer (prepared-by / date / ref) closes it.
 *
 * RELATION TO EditorialMastheadCover: the data contract (`CoverProps`), the
 * verdict source (`getDisplayVerdict`), the KPI machinery
 * (`resolveCoverChipsForReport`), the 1mm-epsilon A4 height clamp
 * (`COVER_MIN_HEIGHT_PX`) and the role-token discipline are reused VERBATIM so
 * the covers stay in lock-step. What differs is the register: a memorandum
 * tombstone rather than a journal masthead.
 *
 * THEME-TOKEN DRIVEN — never the mockup's literal palette. The cream paper,
 * brass accent and navy tombstone in the mockup are the DEFAULTS the matching
 * preset supplies; here every colour is read from the merged `theme`:
 *   - paper        → theme.backgroundColor
 *   - display ink  → reportHeadingInk(theme) (theme.displayFont || theme.headingFont)
 *   - body / sub   → roleColor(theme, 'body') / roleColor(theme,'muted')
 *   - brass accent → roleColor(theme,'accent')   (eyebrow, tombstone rule, verdict val)
 *   - hairlines    → roleColor(theme,'hair' | 'rule')
 *   - tombstone bg → resolveCoverDarkSurface(theme).surface  (theme-derived near-black ink,
 *                    NOT a baked hex), with the on-dark ramp for text on it.
 *
 * Null-safe: an empty standfirst collapses the block, an unscored report shows
 * the tombstone with an "Assessment" word and no headline-metric line, and a
 * legacy report with no metrics renders an em-dash rail rather than throwing.
 * Tenant brand carries through the existing tokens — the eyebrow/tombstone/rail
 * reskin with the active preset (fonts + roleColor roles), and the imprint
 * "prepared by" prefers the report's customer company, then `theme.companyName`.
 */
import React from 'react';
import {
  resolveCoverTitle,
  resolveSiteWordmark,
  resolvePreparedFor,
} from '../../../../lib/reportRendering/journalCoverData';
import { resolveCoverChipsForReport } from '../../../../lib/reportRendering/coverKpiChips';
import { CoverChipField } from './CoverChipField';
import { getDisplayVerdict } from '../../../../lib/reportVerdict';
import { DEFAULT_PRIMARY_COVER_LOGO_URL } from '../../../../lib/reportBranding';
import { roleColor, roleColorAlpha } from '../../../../lib/reportRoleColors';
import { reportTypographySizes, reportHeadingInk } from '../../../../lib/reportSectionHeading';
import { COVER_MIN_HEIGHT_PX, resolveCoverDarkSurface } from '../../../../lib/reportCoverTokens';
import type { CoverProps } from './types';
import { CoverHeaderPlatformCredit, platformCreditToneForTheme } from '../ReportPlatformCredit';

/** Mono label face used for every data/imprint label on the cover (mockup
 *  `'IBM Plex Mono'`). Kept as one constant so the rail / tombstone metric /
 *  footer all read identically. */
const MONO_FONT = "'IBM Plex Mono', monospace";

/** Reference format for the imprint footer — a stable, human "DA-IM-XXXX" stamp
 *  derived deterministically from the report id (NOT model-authored). Falls back
 *  to a generic ref when no id is present. */
function resolveMemorandumRef(report: CoverProps['report']): string {
  const id = report.id?.trim();
  if (!id) return 'Ref. DA-IM';
  // Take the trailing alphanumerics of the id, upper-cased, ≤ 6 chars, so the
  // ref is short, deterministic and traceable to the report.
  const tail = id.replace(/[^a-z0-9]/gi, '').slice(-6).toUpperCase();
  return tail ? `Ref. DA-IM-${tail}` : 'Ref. DA-IM';
}

/** Human display date for the imprint ("June 2026"). Reads the persisted
 *  `report.createdAt`; falls back to now for a freshly-built preview. Never
 *  throws on an unparseable stamp. */
function resolveImprintDate(report: CoverProps['report']): string {
  const raw = report.createdAt;
  const parsed = raw ? new Date(raw) : new Date();
  const date = Number.isNaN(parsed.getTime()) ? new Date() : parsed;
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export const ProspectusCover: React.FC<CoverProps> = ({
  report, theme, branding,
  disclaimer,
}) => {
  // DESIGN PARITY NOTE: like the masthead, the memorandum title page carries its
  // identity in the confidential eyebrow + display title + imprint, not a logo.
  // `effectiveBranding` is resolved for parity but the logo URL is left
  // UNRENDERED by design; `void` marks the omission explicit.
  const effectiveBranding = branding ?? {
    audienceMode: 'internal' as const,
    siteWordmark: resolveSiteWordmark(report, undefined),
    primaryLogoUrl: theme.logoUrl ?? DEFAULT_PRIMARY_COVER_LOGO_URL,
    secondaryLogoUrl: null,
    primaryLogoAlt: theme.companyName ?? 'Primary logo',
    secondaryLogoAlt: 'Customer logo',
  };
  void effectiveBranding;

  // The display name IS the project / site name (mockup "Redhead Industrial
  // Park"); the report-type label rides in the standfirst below.
  const projectName = resolveSiteWordmark(report, branding);
  const reportTypeLabel = resolveCoverTitle(report);

  // Standfirst — the persisted executive summary as the memorandum thesis. We
  // prefer the report's own one-line conclusion recommendation (it reads as a
  // memorandum lede); else fall to the summary. Null-safe: an empty thesis
  // collapses the block.
  const verdict = getDisplayVerdict(report);
  const standfirst = (verdict.savedDecisionPending || verdict.savedDecisionCap || verdict.savedDecisionConflict)
    ? (verdict.savedDecisionConflict ?? verdict.label) :
    report.conclusion?.recommendation?.trim()
    || report.summary?.trim()
    || report.executive?.trim()
    || null;

  // Verdict — the canonical presentation word (same source as every surface).
  const verdictWord =
    verdict.label && verdict.label !== '—' ? verdict.label : 'Assessment';

  // Headline-metric line for the tombstone — the non-null resolved chip values
  // joined with a mid-dot ("312 MW · $0.058/kWh · COD 2028"). Reuses the shared
  // chip resolver so the figures stay unified with the other covers; never
  // invents a metric. Collapses to null when no chip carries a value.
  const chips = resolveCoverChipsForReport(report, 'data-dense');
  const headlineMetrics = chips
    .map((c) => c.value)
    .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
    .slice(0, 3)
    .join('  ·  ');

  // 4-up key-terms rail — the first four resolved chips (mockup Capacity / LCOE
  // / Verdict / In-service). The shared resolver already tailors which chips a
  // report type surfaces; we take the leading four and render mono values via
  // CoverChipField (em-dash on null).
  const railChips = chips.slice(0, 4);

  // Imprint footer fields (mono).
  const preparedBy =
    resolvePreparedFor(report)
    || (theme.companyName?.trim() ? `Prepared by ${theme.companyName.trim()}` : 'Prepared by Datacrate');
  const imprintDate = resolveImprintDate(report);
  const memorandumRef = resolveMemorandumRef(report);

  // The dark tombstone band surface + on-dark ramp are DERIVED from the theme
  // (near-black ink), not a baked navy. The brass left-rule + verdict accent
  // are the theme's own accent. The metric line uses a soft accent-tinted
  // on-dark tone so it whispers under the verdict word like the mockup's
  // `#d8cba6` did against the navy.
  const dark = resolveCoverDarkSurface(theme);

  // Body / standfirst size tracks the resolved body size × the theme's body
  // scale (same density-tracking as the rest of the report).
  const bodyFontSize = `calc(${reportTypographySizes(theme).body} * ${theme.bodyTextScale ?? 1})`;

  return (
    <div
      className="relative flex flex-col"
      data-testid="cover-prospectus"
      data-cover-variant="prospectus"
      data-verdict-tier={verdict.tier}
      style={{
        backgroundColor: theme.backgroundColor,
        color: roleColor(theme, 'body'),
        // Fill the printable A4 box; trim by the SAME 1mm epsilon the outer
        // `#cover` clamp applies (PaginatedReportView) so the bottom-pinned
        // imprint footer never tips the cover over its clamped box (the
        // blank-orphan-page defect the masthead documents).
        minHeight: `max(${COVER_MIN_HEIGHT_PX}px, calc(var(--report-page-height, 297mm) - 14mm - 16mm - 1mm))`,
      }}
    >
      {/* Double-rule confidential eyebrow — 2px ink top rule + 1px hair bottom
          rule, brass (accent) small-caps labels. Canonical memorandum/
          confidential copy; colour + fonts reskin with the active preset. */}
      <div
        className="flex items-baseline justify-between"
        style={{
          borderTop: `2px solid ${roleColor(theme, 'ink')}`,
          borderBottom: `1px solid ${roleColor(theme, 'hair')}`,
          padding: '7px 0',
        }}
        data-testid="cover-prospectus-eyebrow"
      >
        <span
          className="uppercase"
          style={{
            color: roleColor(theme, 'accent'),
            fontFamily: theme.bodyFont,
            fontSize: '10px',
            fontWeight: 600,
            letterSpacing: '0.18em',
          }}
        >
          Investment Memorandum
        </span>
        <span
          className="uppercase"
          style={{
            color: roleColor(theme, 'accent'),
            fontFamily: theme.bodyFont,
            fontSize: '10px',
            fontWeight: 600,
            letterSpacing: '0.18em',
          }}
          data-testid="cover-confidential-tag"
        >
          Private &amp; Confidential
        </span>
      </div>
      {/* GATEWAY POWER platform credit — right-aligned under the eyebrow's
          hairline, in the title's margin-top gap (≥ 40px; zero height). */}
      <CoverHeaderPlatformCredit
        tone={platformCreditToneForTheme(theme)}
        color={roleColor(theme, 'muted')}
        fontFamily={theme.bodyFont}
        testId="cover-prospectus-platform-credit"
      />

      {/* Project name — display serif, headline scale, high-contrast ink. The
          report-type label rides in the standfirst (so the name reads as the
          subject of the memorandum). Display font falls back to headingFont. */}
      <h1
        id="cover-title"
        style={{
          fontFamily: theme.displayFont || theme.headingFont,
          color: reportHeadingInk(theme),
          fontSize: `clamp(38px, 6.5vw, calc(${reportTypographySizes(theme).h2} * 3))`,
          fontWeight: 600,
          lineHeight: 1.02,
          letterSpacing: '-0.01em',
          maxWidth: '18ch',
          marginTop: 'clamp(40px, 14vh, 124px)',
        }}
        data-testid="cover-prospectus-name"
        data-cover-site-name-header="true"
      >
        <span data-testid="cover-site-name-header">{projectName}</span>
      </h1>

      {/* Italic standfirst — the memorandum thesis. The report-type label opens
          the line so the subject is named, then the persisted conclusion/summary
          carries the thesis. Italic serif (headingFont) per the mockup `.pr-sub`. */}
      {standfirst && (
        <p
          className="italic"
          style={{
            fontFamily: theme.headingFont,
            color: roleColor(theme, 'muted'),
            fontSize: `calc(${bodyFontSize} * 1.14)`,
            lineHeight: 1.35,
            maxWidth: '52ch',
            marginTop: '12px',
          }}
          data-testid="cover-prospectus-standfirst"
        >
          <span className="not-italic" style={{ color: roleColor(theme, 'accent'), fontWeight: 600 }}>
            {reportTypeLabel}.{' '}
          </span>
          {standfirst}
        </p>
      )}

      {/* DARK "tombstone" verdict band — the memorandum's gravity move. The
          surface is the theme-derived near-black ink (resolveCoverDarkSurface),
          a brass (accent) left-rule, the deterministic verdict word in the
          display serif (on-dark text), and the headline-metric line in mono
          (soft accent-tinted on-dark). NOT a baked navy/brass. */}
      <div
        className="flex items-center justify-between"
        style={{
          backgroundColor: dark.surface,
          borderLeft: `3px solid ${roleColor(theme, 'accent')}`,
          padding: '14px 18px',
          marginTop: 'clamp(28px, 6vh, 56px)',
          gap: 16,
        }}
        data-testid="cover-prospectus-tombstone"
      >
        <span
          className="uppercase"
          style={{
            fontFamily: theme.displayFont || theme.headingFont,
            color: dark.text,
            fontSize: 'clamp(20px, 3vw, 26px)',
            fontWeight: 600,
            letterSpacing: '0.02em',
          }}
          data-testid="cover-prospectus-verdict"
        >
          {verdictWord}
        </span>
        {headlineMetrics && (
          <span
            className="tabular-nums text-right"
            style={{
              // Soft accent-tinted on-dark tone — the mockup's brass-on-navy
              // metric whisper, derived from the theme accent over the dark
              // surface rather than a baked `#d8cba6`.
              color: roleColorAlpha(theme, 'accent', 0.9),
              fontFamily: MONO_FONT,
              fontSize: 'clamp(11px, 1.4vw, 13px)',
              letterSpacing: '0.02em',
            }}
            data-testid="cover-prospectus-tombstone-metrics"
          >
            {headlineMetrics}
          </span>
        )}
      </div>

      {/* 4-up KEY-TERMS rail — mono small-caps labels, serif/mono values,
          hairline dividers. Reuses the shared chip resolver + CoverChipField so
          the figures stay unified; em-dash on null. */}
      {railChips.length > 0 && (
        <div
          className="grid"
          style={{
            gridTemplateColumns: `repeat(${railChips.length}, minmax(0, 1fr))`,
            borderTop: `1px solid ${roleColor(theme, 'hair')}`,
            marginTop: 'clamp(24px, 5vh, 40px)',
          }}
          data-testid="cover-prospectus-rail"
        >
          {railChips.map((chip, idx) => (
            <div
              key={chip.id}
              data-testid={`cover-prospectus-rail-${chip.id}`}
              style={{
                padding: '12px 16px 12px 0',
                borderRight:
                  idx < railChips.length - 1
                    ? `1px solid ${roleColorAlpha(theme, 'hair', 0.8)}`
                    : undefined,
              }}
            >
              <CoverChipField
                chip={chip}
                report={report}
                theme={theme}
                testIdSuffix={`prospectus-${chip.id}`}
                labelClassName="uppercase"
                valueClassName="tabular-nums"
              />
            </div>
          ))}
        </div>
      )}

      {/* Imprint footer — prepared-by / date / ref, mono, hairline top rule,
          bottom-pinned via mt-auto so it sits at the page edge like the mockup
          `.pr-foot`. */}
      <div
        className="mt-auto flex items-baseline justify-between"
        style={{
          borderTop: `1px solid ${roleColor(theme, 'hair')}`,
          paddingTop: '8px',
          marginTop: 'clamp(24px, 6vh, 48px)',
          fontFamily: MONO_FONT,
          fontSize: '8px',
          letterSpacing: '0.08em',
          color: roleColor(theme, 'muted'),
        }}
        data-testid="cover-prospectus-imprint"
      >
        <span data-testid="cover-prospectus-prepared-by">{preparedBy}</span>
        <span data-testid="cover-prospectus-date">{imprintDate}</span>
        <span className="text-right" data-testid="cover-prospectus-ref">{memorandumRef}</span>
      </div>

      {/* CONFIDENTIAL DISCLAIMER — bottom-pinned beneath the imprint when a
          distinct archetype override is supplied. Null-safe: collapses cleanly
          when the disclaimer is empty. */}
      {disclaimer?.trim() && (
        <div
          className="text-[8px] leading-relaxed"
          style={{ color: roleColor(theme, 'faint'), fontFamily: theme.bodyFont, marginTop: '6px' }}
          data-testid="cover-prospectus-disclaimer"
        >
          {disclaimer}
        </div>
      )}
    </div>
  );
};
